import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Search, Loader2, ShieldAlert, Mic, MicOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Common medical search suggestions
const SEARCH_SUGGESTIONS = [
  // Infectious Disease
  "community acquired pneumonia treatment",
  "UTI antibiotic choice",
  "MRSA treatment options",
  "C. diff management",
  "sepsis antibiotic selection",
  "cellulitis oral antibiotics",
  "strep pharyngitis treatment",
  "HIV prophylaxis PEP",
  
  // Cardiology
  "aspirin primary prevention",
  "statin therapy indications",
  "hypertension first line treatment",
  "atrial fibrillation anticoagulation",
  "heart failure ACE inhibitor",
  "chest pain workup",
  
  // Pulmonology
  "COPD exacerbation treatment",
  "asthma controller therapy",
  "pulmonary embolism treatment",
  
  // Endocrinology
  "type 2 diabetes first line",
  "thyroid nodule management",
  "diabetic ketoacidosis treatment",
  
  // GI
  "H pylori treatment regimen",
  "GERD management",
  "acute pancreatitis treatment",
  
  // Neurology
  "migraine prophylaxis options",
  "stroke tPA window",
  "seizure first line medication",
  
  // Pain/MSK
  "gout acute treatment",
  "low back pain management",
  "osteoporosis treatment",
  
  // Psychiatry
  "depression first line treatment",
  "anxiety medication options",
  "insomnia management",
];

const SearchBar = forwardRef(({ onSearch, isSearching, disabled }, ref) => {
  const [query, setQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SpeechRecognition);
  }, []);

  // Filter suggestions based on query
  useEffect(() => {
    if (query.length >= 2) {
      const queryLower = query.toLowerCase();
      const filtered = SEARCH_SUGGESTIONS.filter(s => 
        s.toLowerCase().includes(queryLower)
      ).slice(0, 6);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [query]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Expose clearQuery method to parent via ref
  useImperativeHandle(ref, () => ({
    clearQuery: () => setQuery(''),
    setQuery: (newQuery) => setQuery(newQuery)
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim() && !isSearching && !disabled) {
      setShowSuggestions(false);
      onSearch(query.trim());
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    // Auto-submit when selecting a suggestion
    if (!isSearching && !disabled) {
      onSearch(suggestion);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const toggleVoiceInput = () => {
    if (!speechSupported) {
      toast.error('Voice input is not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (isListening) {
      // Stop listening
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setQuery(transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please enable it in your browser settings.');
      } else if (event.error !== 'aborted') {
        toast.error('Voice input error. Please try again.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-3xl mx-auto search-bar-wrapper">
      <div className="relative flex items-center" ref={suggestionsRef}>
        <div className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 z-10">
          {isSearching ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Search className="h-5 w-5" />
          )}
        </div>
        <textarea
          ref={inputRef}
          data-testid="search-input"
          placeholder="Ask a clinical question..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
          disabled={isSearching || disabled}
          rows={2}
          className="w-full min-h-[56px] md:min-h-[64px] py-4 pl-12 md:pl-14 pr-28 md:pr-36 text-base md:text-lg rounded-2xl md:rounded-full border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-sky-500 dark:focus:border-sky-400 focus:outline-none shadow-sm focus:shadow-md transition-all resize-none"
          style={{ lineHeight: '1.4' }}
          autoComplete="off"
        />
        
        {/* Autocomplete Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-50 overflow-hidden" data-testid="search-suggestions">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors ${
                  index === selectedIndex
                    ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
                data-testid={`suggestion-${index}`}
              >
                <Search className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" />
                <span>{suggestion}</span>
              </button>
            ))}
          </div>
        )}
        
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10">
          {/* Voice Input Button */}
          {speechSupported && (
            <Button
              type="button"
              variant="ghost"
              onClick={toggleVoiceInput}
              disabled={isSearching || disabled}
              className={`h-9 w-9 md:h-10 md:w-10 p-0 rounded-full transition-all ${
                isListening 
                  ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/70 animate-pulse' 
                  : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              data-testid="voice-input-btn"
              title={isListening ? 'Stop listening' : 'Voice input'}
            >
              {isListening ? (
                <MicOff className="h-4 w-4 md:h-5 md:w-5" />
              ) : (
                <Mic className="h-4 w-4 md:h-5 md:w-5" />
              )}
            </Button>
          )}
          <Button
            data-testid="search-button"
            type="submit"
            disabled={!query.trim() || isSearching || disabled}
            className="h-9 md:h-10 px-4 md:px-6 rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-sky-600 dark:hover:bg-sky-500 text-white font-medium text-sm md:text-base transition-all"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </div>
      
      {/* PRIVACY: PHI/PII Warning */}
      <div className="flex items-center justify-center gap-2 mt-3 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 rounded-full py-2 px-4 text-sm" data-testid="phi-warning">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <span><strong>Reminder:</strong> No Patient Health Information (PHI). Use for educational research only.</span>
      </div>
      
      {isListening && (
        <p className="text-center text-sm text-red-500 dark:text-red-400 mt-3 animate-pulse">
          🎤 Listening... Speak your question
        </p>
      )}
      
      {isSearching && (
        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4 loading-pulse">
          Searching PubMed and guidelines...
        </p>
      )}
    </form>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
